/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";

const loadScript = (src, id) =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

/**
 * Full-page travel order payment checkout (Stripe Hosted Checkout or Razorpay).
 * Expects location.state: { paymentTransactionId, gateway, amount, currency, returnTo?, orderId? }
 */
export default function PaymentCheckoutPage() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const paymentTransactionId = state.paymentTransactionId;
  const gateway = state.gateway;
  const amount = state.amount;
  const currency = state.currency || "GBP";
  const returnTo = state.returnTo || "/travel/orders";
  const orderId = state.orderId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checkout, setCheckout] = useState(null);

  const goBack = useCallback(() => {
    navigate(returnTo, {
      state: orderId ? { paymentCancelled: true, orderId } : undefined,
    });
  }, [navigate, returnTo, orderId]);

  const handleFailure = useCallback(
    async (message) => {
      setError(message || "Payment failed.");
      if (paymentTransactionId) {
        try {
          await axiosInstance.post(`/payment-gateways/${paymentTransactionId}/failed`);
        } catch (e) {
          console.error("Failed to record payment failure:", e);
        }
      }
    },
    [axiosInstance, paymentTransactionId]
  );

  const handleCancel = useCallback(async () => {
    if (paymentTransactionId) {
      try {
        await axiosInstance.post(`/payment-gateways/${paymentTransactionId}/cancelled`);
      } catch (e) {
        console.error("Failed to record payment cancellation:", e);
      }
    }
    goBack();
  }, [axiosInstance, paymentTransactionId, goBack]);

  const navigatePaidSuccess = useCallback(() => {
    navigate(returnTo, {
      state: {
        paymentSuccess: true,
        orderId,
        paymentTransactionId,
      },
      replace: true,
    });
  }, [navigate, returnTo, orderId, paymentTransactionId]);

  const confirmOnServer = useCallback(
    async (payload) => {
      try {
        await axiosInstance.post("/payment-gateways/confirm", payload);
        navigatePaidSuccess();
      } catch (err) {
        // Gateway charge may already be Paid while confirm waits on emails/PDF.
        const message = String(err?.message || "").toLowerCase();
        const timedOut =
          err?.code === "ECONNABORTED" || message.includes("timeout");
        if (timedOut && paymentTransactionId) {
          try {
            const res = await axiosInstance.post("/payment-gateways/checkout", {
              payment_transaction_id: paymentTransactionId,
              gateway: gateway || "stripe",
            });
            if (res.data?.already_paid) {
              navigatePaidSuccess();
              return;
            }
          } catch (_) {
            // Fall through to original error.
          }
        }
        throw err;
      }
    },
    [axiosInstance, gateway, navigatePaidSuccess, paymentTransactionId]
  );

  const startCheckout = useCallback(async () => {
    if (!paymentTransactionId || !gateway) return;
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.post("/payment-gateways/checkout", {
        payment_transaction_id: paymentTransactionId,
        gateway,
      });
      if (res.data?.already_paid) {
        navigate(returnTo, {
          state: { paymentSuccess: true, orderId, paymentTransactionId },
          replace: true,
        });
        return;
      }
      if (gateway === "stripe" && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setCheckout(res.data);
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || "Unable to start payment.";
      setError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, gateway, navigate, orderId, paymentTransactionId, returnTo]);

  useEffect(() => {
    if (!paymentTransactionId || !gateway) {
      setError("Missing payment details. Start checkout again from the travel order.");
      return;
    }
    startCheckout();
  }, [paymentTransactionId, gateway, startCheckout]);

  const payWithRazorpay = async () => {
    if (!checkout?.razorpay_order_id) {
      setError("Razorpay checkout is not ready.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await loadScript("https://checkout.razorpay.com/v1/checkout.js", "razorpay-js");
      const keyId = checkout.razorpay_key_id;
      const options = {
        key: keyId,
        amount: Math.round(Number(amount) * 100),
        currency: (currency || "INR").toUpperCase(),
        name: "Pearl CRM Travel",
        description: `Travel order payment #${paymentTransactionId}`,
        order_id: checkout.razorpay_order_id,
        handler: async (response) => {
          try {
            await confirmOnServer({
              payment_transaction_id: paymentTransactionId,
              gateway: "razorpay",
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
          } catch (err) {
            await handleFailure(err?.message || "Could not confirm Razorpay payment.");
          }
        },
        modal: {
          ondismiss: () => {
            handleCancel();
          },
        },
        theme: { color: "#1A73E8" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", async (resp) => {
        await handleFailure(resp?.error?.description || "Razorpay payment failed.");
      });
      rzp.open();
    } catch (err) {
      await handleFailure(err?.message || "Razorpay payment failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={6} px={2} maxWidth={640} mx="auto">
        <MDBox display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={handleCancel}>
            <ArrowBack />
          </IconButton>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Complete payment
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {gateway === "razorpay" ? "Razorpay" : "Stripe"} · {currency}{" "}
              {Number(amount || 0).toFixed(2)}
            </MDTypography>
          </MDBox>
        </MDBox>

        <Card sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {(loading || (gateway === "stripe" && !error)) && (
            <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
              <CircularProgress />
              <MDTypography variant="caption" color="text">
                {gateway === "stripe" ? "Redirecting to Stripe Checkout…" : "Preparing checkout…"}
              </MDTypography>
            </MDBox>
          )}

          {gateway === "razorpay" && checkout && (
            <>
              <MDTypography variant="body2" color="text" mb={2}>
                Click Pay now to open the Razorpay checkout securely.
              </MDTypography>
              <MDBox display="flex" justifyContent="flex-end" gap={1} mt={3}>
                <MDButton color="secondary" onClick={handleCancel} disabled={loading}>
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={payWithRazorpay}
                  disabled={loading || !checkout}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Pay now"}
                </MDButton>
              </MDBox>
            </>
          )}

          {error && gateway === "stripe" && (
            <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <MDButton color="secondary" onClick={handleCancel}>
                Cancel
              </MDButton>
              <MDButton variant="gradient" color="info" onClick={startCheckout}>
                Retry Stripe Checkout
              </MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
